import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle, XCircle, Warning, Package, Buildings, Truck, CalendarBlank, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/dateUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public URL: /teyit/:token — kimlik doğrulaması gerektirmez.
// Tedarikçi PDF üstündeki QR karekodu telefonuyla okuttuğunda buraya gelir.

const fmtMoney = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const CORRECTION_FIELDS = [
  { key: "price", label: "Fiyat" },
  { key: "quantity", label: "Miktar" },
  { key: "delivery_date", label: "Teslim Tarihi" },
  { key: "payment_terms", label: "Ödeme Koşulu" },
  { key: "address", label: "Adres / Teslimat Yeri" },
  { key: "other", label: "Diğer" },
];

export default function PublicConfirmation() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [action, setAction] = useState(""); // "confirm" | "reject" | ""
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [correctionFields, setCorrectionFields] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const toggleCorrectionField = (key) => {
    setCorrectionFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/order-confirmation/${token}`);
        setData(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Teyit bağlantısı geçersiz veya süresi dolmuş.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async () => {
    if (!name.trim() || name.trim().length < 2) {
      toast.error("Lütfen ad ve soyadınızı giriniz.");
      return;
    }
    if (action === "reject" && correctionFields.length === 0) {
      toast.error("Düzeltme talep etmek için en az bir alan seçmelisiniz.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/order-confirmation/${token}`, {
        action, name: name.trim(), note: note.trim(),
        correction_fields: action === "reject" ? correctionFields : [],
      });
      toast.success(r.data.status === "confirmed" ? "Teyidiniz kaydedildi." : "Düzeltme talebiniz iletildi.");
      const s = await axios.get(`${API}/public/order-confirmation/${token}`);
      setData(s.data);
      setAction("");
      setName("");
      setNote("");
      setCorrectionFields([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem tamamlanamadı.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="text-xs uppercase tracking-[0.25em] font-bold text-slate-500">Yükleniyor…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 shadow-sm p-8 rounded-none sm:rounded-sm text-center">
          <Warning size={48} className="mx-auto text-amber-500 mb-3" />
          <div className="text-lg font-heading font-bold text-slate-800">Bağlantı geçersiz</div>
          <div className="text-sm text-slate-600 mt-2">{error}</div>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const isConfirmed = data.confirmation_status === "confirmed";
  const isRejected = data.confirmation_status === "rejected";
  const isActioned = isConfirmed || isRejected;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-[#1B4776] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold opacity-80">MSS Taahhüt İnşaat</div>
          <div className="text-2xl sm:text-3xl font-heading font-bold mt-1">Sipariş Teyit Formu</div>
          <div className="text-sm opacity-90 mt-1 font-mono">{data.code}</div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {/* Durum rozeti (aksiyon alındıysa) */}
        {isActioned && (
          <div
            data-testid="pc-status-banner"
            className={`p-4 border rounded-none sm:rounded-sm flex items-start gap-3 ${
              isConfirmed
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : "bg-rose-50 border-rose-200 text-rose-900"
            }`}
          >
            {isConfirmed ? <CheckCircle size={28} weight="fill" /> : <XCircle size={28} weight="fill" />}
            <div className="flex-1">
              <div className="font-bold text-sm uppercase tracking-widest">
                {isConfirmed ? "Teyit Edildi" : "Düzeltme Talep Edildi"}
              </div>
              <div className="text-xs mt-1">
                {data.confirmation_confirmed_by_name} • {fmtDate(data.confirmation_action_at)}
              </div>
              {isRejected && data.confirmation_correction_fields?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="pc-correction-chips">
                  {data.confirmation_correction_fields.map((k) => {
                    const label = CORRECTION_FIELDS.find((f) => f.key === k)?.label || k;
                    return (
                      <span key={k} className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 bg-white/70 border border-rose-300 text-rose-800 rounded-none sm:rounded-sm">
                        {label}
                      </span>
                    );
                  })}
                </div>
              )}
              {data.confirmation_note && (
                <div className="text-sm mt-2 italic">&quot;{data.confirmation_note}&quot;</div>
              )}
            </div>
          </div>
        )}

        {/* Genel bilgi */}
        <div className="bg-white border border-slate-200 shadow-sm rounded-none sm:rounded-sm p-5">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-3">Genel Bilgi</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow icon={<Buildings size={14} />} label="Tedarikçi" value={data.supplier_name} />
            <InfoRow icon={<Package size={14} />} label="Şantiye" value={data.site_name} />
            <InfoRow icon={<CalendarBlank size={14} />} label="Teslim Tarihi" value={data.delivery_date_text || "-"} />
            <InfoRow icon={<Truck size={14} />} label="Nakliye" value={data.shipping_note || "-"} />
            <InfoRow icon={<User size={14} />} label="İlgili Kişi" value={data.contact_person ? `${data.contact_person}${data.contact_phone ? " • " + data.contact_phone : ""}` : "-"} />
            <InfoRow label="Ödeme Türü" value={data.payment_type || "-"} />
            <InfoRow label="Vade Şekli" value={data.payment_terms_text || "-"} />
            <div className="md:col-span-2">
              <InfoRow label="Teslimat Adresi" value={data.delivery_address || "-"} />
            </div>
          </div>
        </div>

        {/* İlave Açıklamalar — çok satırlı */}
        {data.additional_notes && data.additional_notes.trim() && (
          <div className="bg-white border border-slate-200 shadow-sm rounded-none sm:rounded-sm p-5" data-testid="pc-additional-notes">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">İlave Açıklamalar</div>
            <div className="text-sm text-slate-800 whitespace-pre-line leading-relaxed">
              {data.additional_notes}
            </div>
          </div>
        )}

        {/* Kalem tablosu */}
        <div className="bg-white border border-slate-200 shadow-sm rounded-none sm:rounded-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">
            Kalemler
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2">Malzeme</th>
                  <th className="text-center px-3 py-2">Birim</th>
                  <th className="text-right px-3 py-2">Miktar</th>
                  <th className="text-right px-3 py-2">Birim Fiyat</th>
                  <th className="text-center px-3 py-2">KDV</th>
                  <th className="text-right px-4 py-2">Tutar (KDV Hariç)</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-slate-800">{it.material_name}</td>
                    <td className="px-3 py-2 text-center text-slate-600">{it.unit}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(it.quantity)}</td>
                    <td className="px-3 py-2 text-right font-mono">₺{fmtMoney(it.unit_price)}</td>
                    <td className="px-3 py-2 text-center text-slate-600">%{it.vat_rate}</td>
                    <td className="px-4 py-2 text-right font-mono">₺{fmtMoney(it.line_total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 text-sm">
                <tr className="border-t border-slate-200">
                  <td colSpan={5} className="px-4 py-2 text-right text-slate-600">Ara Toplam</td>
                  <td className="px-4 py-2 text-right font-mono">₺{fmtMoney(data.ara_toplam)}</td>
                </tr>
                <tr>
                  <td colSpan={5} className="px-4 py-2 text-right text-slate-600">Toplam KDV</td>
                  <td className="px-4 py-2 text-right font-mono">₺{fmtMoney(data.kdv_toplam)}</td>
                </tr>
                <tr className="border-t border-slate-300">
                  <td colSpan={5} className="px-4 py-3 text-right font-bold uppercase tracking-widest text-slate-700">Genel Toplam</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-lg text-[#1B4776]">₺{fmtMoney(data.toplam)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Aksiyonlar */}
        {!isActioned && (
          <div className="bg-white border border-slate-200 shadow-sm rounded-none sm:rounded-sm p-5">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-3">Onayınız</div>
            {!action ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  data-testid="pc-btn-confirm"
                  onClick={() => setAction("confirm")}
                  className="rounded-none sm:rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-14 text-sm uppercase tracking-widest font-bold"
                >
                  <CheckCircle size={20} weight="fill" className="mr-2" /> Teyit Ediyorum
                </Button>
                <Button
                  data-testid="pc-btn-reject"
                  onClick={() => setAction("reject")}
                  variant="outline"
                  className="rounded-none sm:rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50 h-14 text-sm uppercase tracking-widest font-bold"
                >
                  <XCircle size={20} className="mr-2" /> Düzeltme Talep Ediyorum
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className={`p-3 border rounded-none sm:rounded-sm text-sm ${
                  action === "confirm" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"
                }`}>
                  {action === "confirm"
                    ? "Bu sipariş teyidini onaylıyorsunuz. Devam etmek için lütfen kimlik bilgilerinizi giriniz."
                    : "Düzeltme talep edildiğini bildiriyorsunuz. Aşağıdan hangi alanların düzeltilmesi gerektiğini işaretleyin ve kimlik bilgilerinizi girin."}
                </div>
                {action === "reject" && (
                  <div data-testid="pc-correction-picker" className="border border-rose-200 bg-rose-50/40 p-3 rounded-none sm:rounded-sm">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-rose-800 mb-2">
                      Düzeltilecek Alanlar <span className="text-rose-600 normal-case">(en az bir tane)</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {CORRECTION_FIELDS.map((f) => (
                        <label
                          key={f.key}
                          data-testid={`pc-correction-${f.key}`}
                          className={`flex items-center gap-2 px-3 py-2 border cursor-pointer transition-colors rounded-none sm:rounded-sm ${
                            correctionFields.includes(f.key)
                              ? "bg-rose-100 border-rose-400 text-rose-900"
                              : "bg-white border-slate-200 hover:bg-rose-50 hover:border-rose-300"
                          }`}
                        >
                          <Checkbox
                            checked={correctionFields.includes(f.key)}
                            onCheckedChange={() => toggleCorrectionField(f.key)}
                          />
                          <span className="text-sm font-bold">{f.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Ad Soyad</Label>
                  <Input
                    data-testid="pc-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Örn: Ahmet Yılmaz"
                    className="mt-1 rounded-none sm:rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">
                    Not {action === "reject" && <span className="text-rose-600 normal-case">(Detay yazabilirsiniz, opsiyonel)</span>}
                  </Label>
                  <Textarea
                    data-testid="pc-note"
                    rows={3}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={action === "confirm" ? "İsteğe bağlı — ek bir notunuz varsa yazabilirsiniz." : "Örn: Fiyat %5 yüksek görünüyor, teslim tarihi 20 gün öne alınabilir mi?"}
                    className="mt-1 rounded-none sm:rounded-sm"
                  />
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => { setAction(""); setNote(""); setCorrectionFields([]); }}
                    className="rounded-none sm:rounded-sm"
                    data-testid="pc-back"
                  >
                    Geri
                  </Button>
                  <Button
                    data-testid="pc-submit"
                    onClick={submit}
                    disabled={submitting}
                    className={`rounded-none sm:rounded-sm text-white text-xs uppercase tracking-widest font-bold ${
                      action === "confirm" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"
                    }`}
                  >
                    {submitting ? "Gönderiliyor…" : (action === "confirm" ? "Teyidi Kaydet" : "Talebi Gönder")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="text-center text-[10px] uppercase tracking-[0.25em] text-slate-400 pt-4 pb-8">
          MSS Yönetim Sistemi · Güvenli Doğrulama Bağlantısı
        </div>
      </div>
    </div>
  );
}

const InfoRow = ({ icon, label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5">
      {icon}{label}
    </div>
    <div className="mt-1 text-sm text-slate-800">{value || "-"}</div>
  </div>
);
